/**
 * Shared Device ID Service
 * Centralized device ID management for Kit19SSO and consumer apps
 * 
 * All apps on the same browser should use the SAME device ID
 * This device ID is generated by Kit19SSO and shared via localStorage
 */

/**
 * Storage key for the device ID (shared across all Kit19 apps)
 */
const DEVICE_ID_KEY = 'kit19_device_id';

/**
 * Get or create a unique device ID for this browser
 * All Kit19 apps on the same browser will use this SAME device ID
 * 
 * Generation happens only once per browser, then reused
 * 
 * @returns {string} Device ID (format: timestamp_random_userAgent)
 */
export const getOrCreateDeviceId = () => {
  // Check if device ID already exists (from Kit19SSO or another consumer app)
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  
  if (!deviceId) {
    // Generate unique device ID for this browser
    // This becomes the shared ID for all Kit19 apps on this browser
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log('[Device ID Service] Generated new device ID:', deviceId);
  } else {
    console.log('[Device ID Service] Using existing device ID:', deviceId);
  }
  
  return deviceId;
};

/**
 * Generate a unique device ID
 * Format: timestamp_randomString_userAgentHash
 * 
 * @returns {string} Unique device ID
 */
const generateDeviceId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  
  // Hash of user agent to fingerprint the browser
  let userAgentHash;
  try {
    const userAgent = navigator.userAgent;
    // Simple hash of user agent
    userAgentHash = btoa(userAgent).substring(0, 8);
  } catch (e) {
    userAgentHash = 'unknown';
  }
  
  return `${timestamp}_${random}_${userAgentHash}`;
};

/**
 * Get the current device ID without creating one
 * Returns null if not yet created
 * 
 * @returns {string|null} Device ID or null
 */
export const getDeviceIdIfExists = () => {
  return localStorage.getItem(DEVICE_ID_KEY);
};

/**
 * Clear the device ID (for logout/testing)
 * NOTE: Only call this during complete logout from Kit19SSO
 * Consumer apps should NOT clear this independently
 */
export const clearDeviceId = () => {
  localStorage.removeItem(DEVICE_ID_KEY);
  console.log('[Device ID Service] Device ID cleared');
};

/**
 * Get device ID metadata
 * Useful for debugging cross-app issues
 * 
 * @returns {object} Device info {deviceId, createdAt, browser}
 */
export const getDeviceInfo = () => {
  const deviceId = getDeviceIdIfExists();
  
  if (!deviceId) {
    return {
      deviceId: null,
      status: 'not_initialized'
    };
  }
  
  // Extract timestamp from device ID
  const parts = deviceId.split('_');
  const timestamp = parts[0];
  const createdAt = new Date(parseInt(timestamp)).toISOString();
  
  return {
    deviceId,
    createdAt,
    browser: navigator.userAgent.substring(0, 50),
    status: 'initialized'
  };
};

/**
 * Verify device ID consistency across windows/tabs
 * All tabs should have the SAME device ID
 * 
 * @returns {boolean} true if consistent, false if mismatch
 */
export const isDeviceIdConsistent = () => {
  const currentId = getDeviceIdIfExists();
  
  if (!currentId) {
    return true; // No ID yet, nothing to check
  }
  
  // In a real app, you could verify with a shared service
  // For now, just verify it's in localStorage consistently
  return localStorage.getItem(DEVICE_ID_KEY) === currentId;
};

const deviceIdServiceExport = {
  getOrCreateDeviceId,
  getDeviceIdIfExists,
  clearDeviceId,
  getDeviceInfo,
  isDeviceIdConsistent
};

export default deviceIdServiceExport;
